import { useState } from 'react'
import { Alert, Button, Divider, Stack, Typography } from '@mui/material'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import SmartSelect from '../../../generic/smartSelect/SmartSelect'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { formatTemplateVersion, TEMPLATE_LINK_ACTION } from './templateLinks'

/** The index a new template version starts at (DL24). */
const DEFAULT_INDEX = 'DRAFT'

/**
 * TemplateLinkDecisionDialog — story 224, HU-05 (S191-S195, S199; DL15, RN-07.3, RN-07.4): the question asked when the user has edited
 * a unit linked to a template: publish the changes as a new version of the template, or unlink the unit. One row per unit without a
 * decision, the ROOT first (the caller orders them). Built on the generic `SmartDialog`, like the impact dialog.
 *
 * Choosing an option decides THAT row at once (`onDecide`): the caller keeps the decision pending in the editor and removes the row;
 * Cancel closes the dialog and leaves what was already decided. Nothing is written here, nor by the caller: Save writes it.
 *
 * @param {object} props
 * @param {object[]} props.rows `{unitId, isRoot, where, templateCode, templateVersion, pendingTargetVersion, newerVersion}` — `where` is
 * the unit as the user reads it ("Whole workflow", "WELD / PAINT"); `pendingTargetVersion` is the target of an update accepted for the unit
 * (0 when none, H14); `newerVersion` is the newest version of the series when it is newer than the base of a publication (0 when none, G7)
 * @param {boolean} props.single the dialog asks about one unit (the accept of a `SubworkflowDialog`) and not about the whole Save
 * @param {boolean} props.useIndex `USE_WORKFLOW_INDEX`: without it there is no index selector and the version is born APPROVED (RN-07.10)
 * @param {{name: string}[]} props.indexValues the indexes the user is allowed
 * @param {function(object, string, string)} props.onDecide `(row, action, indexValueName)`
 * @param {function()} props.onCancel
 */
const TemplateLinkDecisionDialog = ({ rows = [], single = false, useIndex = false, indexValues = [], onDecide = () => {}, onCancel = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [indexByUnit, setIndexByUnit] = useState({})
  if (rows.length === 0) {
    return null
  }
  const indexNames = indexValues.map((index) => index.name)
  const defaultIndex = indexNames.includes(DEFAULT_INDEX) ? DEFAULT_INDEX : indexNames[0] || ''
  const indexOf = (row) => indexByUnit[row.unitId] || defaultIndex

  return (
    <SmartDialog
      setOpen
      title={{ render: <Typography variant='h6'>{t_(single ? 'This unit is linked to a template' : 'Units linked to templates have changes')}</Typography> }}
      cancel
      cancelLabel='Cancel'
      cancelCallback={onCancel}
      accept={false}
      autoClose={false}
      paperWidth='50rem'
      renderComponent={
        <Stack spacing={2} sx={{ textAlign: 'left' }} data-testid='template-decision-dialog'>
          {rows.map((row, position) => (
            <Stack key={row.unitId} spacing={1.5} data-testid='decision-row' data-unit={row.unitId}>
              {position > 0 ? <Divider /> : null}
              <Typography data-testid='decision-unit'>
                {row.where + ' · ' + t_('Template') + ' ' + row.templateCode + ' v' + formatTemplateVersion(row.templateVersion) +
                  (row.pendingTargetVersion > 0 ? ' → v' + formatTemplateVersion(row.pendingTargetVersion) : '')}
              </Typography>
              <Typography variant='body2' data-testid='decision-question'>
                {row.pendingTargetVersion > 0
                  ? t_('You changed a unit linked to template {{code}} with a pending update v{{from}} → v{{to}}. What do you want to do with these changes?', {
                    code: row.templateCode,
                    from: formatTemplateVersion(row.templateVersion),
                    to: formatTemplateVersion(row.pendingTargetVersion)
                  })
                  : t_('You changed a unit linked to template {{code}} v{{version}}. What do you want to do with these changes?', {
                    code: row.templateCode,
                    version: formatTemplateVersion(row.templateVersion)
                  })}
              </Typography>
              <Stack spacing={1} data-testid='decision-publish'>
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Button variant='outlined' onClick={() => onDecide(row, TEMPLATE_LINK_ACTION.PUBLISH, useIndex ? indexOf(row) : '')}>
                    {t_('Publish as a new version of the template')}
                  </Button>
                  {useIndex
                    ? (
                      <SmartSelect
                        label={t_('Index of the new template version')}
                        value={indexOf(row)}
                        onChange={(value) => setIndexByUnit((previous) => ({ ...previous, [row.unitId]: value }))}
                        selectableOptions={indexNames}
                        optionLabelTransform={(option) => t_(option)}
                        disableClearable
                        size='small'
                        width='14rem'
                      />
                      )
                    : null}
                </Stack>
                {row.pendingTargetVersion > 0
                  ? (
                    <Typography variant='body2' color='text.secondary' data-testid='decision-based-on'>
                      {t_('The new version will be based on v{{version}} plus your changes', { version: formatTemplateVersion(row.pendingTargetVersion) })}
                    </Typography>
                    )
                  : null}
                {row.newerVersion > 0
                  ? (
                    <Alert severity='warning' data-testid='decision-newer-warning'>
                      {t_('There are newer versions of the template (v{{version}}); their changes will not be included', { version: formatTemplateVersion(row.newerVersion) })}
                    </Alert>
                    )
                  : null}
              </Stack>
              <Stack spacing={0.5} data-testid='decision-unlink'>
                <Button variant='outlined' color='warning' sx={{ alignSelf: 'flex-start' }} onClick={() => onDecide(row, TEMPLATE_LINK_ACTION.UNLINK, '')}>
                  {t_('Unlink from the template')}
                </Button>
                <Typography variant='body2' color='text.secondary' data-testid='decision-unlink-text'>
                  {row.isRoot
                    ? t_('The workflow stays as it is, without a template, and will no longer be notified of new versions')
                    : t_('The subworkflow stays in this workflow as it is, without a template, and will no longer be notified of new versions')}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      }
    />
  )
}

export default TemplateLinkDecisionDialog
